// Program Settings rules. Words: PROGRAM_SETTINGS_WORDS.md. Mockup: MOCKUP_PROGRAM_SETTINGS_APPROVED.html
// (both approved 2026-10-01). Glove gate retired 2026-10-05 (GLOVES_WORDS.md): no Gloves section.
// Data names: safetag_config_copy.py (settings/program, contacts with scope "program").
// Pure: no database. Shared by the page, the server steps (functions/src/settings.ts) and the tests.

// ---- What Firestore holds: companies/{company}/settings/program ----

export interface ProgramSettings {
  orgName: string;
  programContactEmail: string;
  eewpEnabled: boolean;
  eewpNotifyEmail: boolean;
  eewpNotifyText: boolean;
  eewpRequiredCoverRemoval: boolean;
  eewpRequiredTier2: boolean;
  eewpRequiredTier3Approach: boolean;
  lotoAwarenessEmail: boolean;
  ieCeilingCal: number;
  twoPersonRuleVolts: number;
  tamperSealAuditYears: number;
  taskAuthCycleDays: number;
  /** Piece 6: a contractor's sponsor email must be on one of these (alltech.com and the brand domains). */
  companyEmailDomains?: string[];
  /** Last change per field: who and when. Written by server code. */
  changes?: Record<string, { from: unknown; to: unknown; byName: string; at: string }>;
}

// ---- Switches ----

export type SwitchField =
  | "eewpEnabled"
  | "eewpNotifyEmail"
  | "eewpNotifyText"
  | "eewpRequiredCoverRemoval"
  | "eewpRequiredTier2"
  | "eewpRequiredTier3Approach"
  | "lotoAwarenessEmail";

export interface SwitchDef {
  field: SwitchField;
  label: string;
  hint: string;
  /** What the confirm says happens in the field, on and off. */
  onLine: string;
  offLine: string;
}

export const EEWP_SWITCHES: SwitchDef[] = [
  { field: "eewpEnabled", label: "EEWP On", hint: "QEWs can raise an energized work permit from the JSP.", onLine: "QEWs can raise an energized work permit from the JSP flow.", offLine: "The JSP flow stops offering energized work permits at every site." },
  { field: "eewpNotifyEmail", label: "Notify By Email", hint: "Approvers get each permit by email.", onLine: "Permit approvers get an email for each new permit.", offLine: "Permit approvers stop getting emails for new permits." },
];

/**
 * Notify By Text is off the EEWP page (mockup rev 2b): there is no text route.
 * The field stays in settings so the switch can come back with one.
 */
export const EEWP_TEXT_SWITCH: SwitchDef = { field: "eewpNotifyText", label: "Notify By Text", hint: "Approvers get the permit by text.", onLine: "Permit approvers get a text for each new permit.", offLine: "Permit approvers stop getting texts for new permits." };

export const EEWP_REQUIRED: SwitchDef[] = [
  { field: "eewpRequiredCoverRemoval", label: "Diagnostic Cover Removal", hint: "IR bolted cover removal offers an EEWP. On: required.", onLine: "Removing bolted covers for IR needs an approved EEWP first, at every site.", offLine: "Removing bolted covers for IR offers an EEWP. It is no longer required." },
  { field: "eewpRequiredTier2", label: "Tier 2", hint: "Troubleshooting stays permit-free while off.", onLine: "Every Tier 2 troubleshooting job needs an approved EEWP first, at every site.", offLine: "Tier 2 troubleshooting no longer needs an EEWP." },
  { field: "eewpRequiredTier3Approach", label: "Tier 3 Approach", hint: "Energized work", onLine: "Every Tier 3 energized approach needs an approved EEWP first, at every site.", offLine: "Tier 3 approach no longer needs an EEWP." },
];

export const LOTO_SWITCHES: SwitchDef[] = [
  {
    field: "lotoAwarenessEmail",
    label: "Awareness Email",
    hint: "Emails the QEW and the site's LOTO awareness contacts when a JSP is submitted. One Work Started email per JSP.",
    onLine: "Each submitted JSP emails the QEW and the site's LOTO awareness contacts.",
    offLine: "Submitted JSPs stop sending the Work Started email.",
  },
];

export const ALL_SWITCHES: SwitchDef[] = [...EEWP_SWITCHES, EEWP_TEXT_SWITCH, ...EEWP_REQUIRED, ...LOTO_SWITCHES];

export function isSwitchField(v: unknown): v is SwitchField {
  return ALL_SWITCHES.some((s) => s.field === v);
}

// ---- Safety limits ----

export type LimitField = "ieCeilingCal" | "twoPersonRuleVolts" | "tamperSealAuditYears" | "taskAuthCycleDays";

export interface LimitDef {
  field: LimitField;
  label: string;
  rule: string;
  unit: string;
  min: number;
  max: number;
  whole: boolean;
  /** Why the max holds, shown when someone goes over it. */
  maxWhy: string;
  /** What moves in the field, for the confirm. */
  effect: (from: number, to: number) => string;
}

export const TASK_AUTH_CAP = 1095;

export const LIMITS: LimitDef[] = [
  {
    field: "ieCeilingCal",
    label: "IE Ceiling",
    rule: "No PPE at or above this. Work is forced to ESWC.",
    unit: "cal/cm²",
    min: 1.2,
    max: 100,
    whole: false,
    maxWhy: "Keep the ceiling between 1.2 and 100 cal/cm².",
    effect: (from, to) => `From ${fmt(from)} to ${fmt(to)} cal/cm². Any asset at ${fmt(to)} or above will be forced to ESWC on the next JSP, at every site.`,
  },
  {
    field: "twoPersonRuleVolts",
    label: "Two-Person Rule",
    rule: "A second qualified person at or above this voltage.",
    unit: "V",
    min: 50,
    max: 15000,
    whole: true,
    maxWhy: "Keep the threshold between 50 and 15,000 V.",
    effect: (from, to) => `From ${fmt(from)} to ${fmt(to)} V. Jobs at ${fmt(to)} V and up need a second qualified person, at every site.`,
  },
  {
    field: "tamperSealAuditYears",
    label: "Tamper Seal Audit",
    rule: "How often sealed equipment is re-checked.",
    unit: "yr",
    min: 1,
    max: 10,
    whole: true,
    maxWhy: "Keep the audit between 1 and 10 years.",
    effect: (from, to) => `From every ${fmt(from)} to every ${fmt(to)} ${to === 1 ? "year" : "years"}. Sealed equipment comes due on the new schedule.`,
  },
  {
    field: "taskAuthCycleDays",
    label: "Task Authorization Cycle",
    rule: "Max 1,095 days. NFPA 70E 110.2(B)(3).",
    unit: "days",
    min: 30,
    max: TASK_AUTH_CAP,
    whole: true,
    maxWhy: "NFPA 70E 110.2(B)(3) sets 3 years as the outer limit. Enter 1,095 or less.",
    effect: (from, to) => `From ${fmt(from)} to ${fmt(to)} days. My Tasks turns amber 30 days before each task lapses on the new cycle, at every site.`,
  },
];

export function isLimitField(v: unknown): v is LimitField {
  return LIMITS.some((l) => l.field === v);
}

export function fmt(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Why a new limit is refused. Null when it can go. The server runs the same check. */
export function limitProblem(field: LimitField, raw: string | number): string | null {
  const def = LIMITS.find((l) => l.field === field)!;
  const s = String(raw).replace(/,/g, "").trim();
  if (!s) return `Enter a value for ${def.label}.`;
  const n = Number(s);
  if (!Number.isFinite(n)) return "Enter a number.";
  if (def.whole && !Number.isInteger(n)) return "Enter a whole number.";
  if (n > def.max) return def.field === "taskAuthCycleDays" ? `Max is 1,095 days (3 years). ${def.maxWhy}` : def.maxWhy;
  if (n < def.min) return def.maxWhy;
  return null;
}

export function limitValue(raw: string | number): number {
  return Number(String(raw).replace(/,/g, "").trim());
}

// ---- Program ----

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function programInfoProblem(orgName: string, email: string): string | null {
  if (!orgName.trim()) return "Enter the organization name.";
  if (orgName.trim().length > 80) return "Keep the name under 80 characters.";
  if (!EMAIL.test(email.trim())) return "Enter a working email address.";
  return null;
}

// ---- Program Contacts ----

export interface ProgramUsedFor {
  jspAlerts: boolean;
  trainingAlerts: boolean;
  observationAlerts: boolean;
  eewpObserver: boolean;
}

export const PROGRAM_USED_FOR: { key: keyof ProgramUsedFor; label: string; hint: string }[] = [
  { key: "jspAlerts", label: "JSP Alerts", hint: "Every JSP submitted. Glove alerts too." },
  { key: "trainingAlerts", label: "Training Alerts", hint: "Training checks and records" },
  { key: "observationAlerts", label: "Observation Alerts", hint: "Observations submitted from the field" },
  { key: "eewpObserver", label: "EEWP Observer", hint: "Copied on energized work permits" },
];

export const NO_PROGRAM_TICKS: ProgramUsedFor = { jspAlerts: false, trainingAlerts: false, observationAlerts: false, eewpObserver: false };

export interface ProgramContact {
  contactId: string;
  scope: "program";
  name: string;
  email: string;
  isActive: boolean;
  usedFor: ProgramUsedFor;
}

export interface ProgramContactInput {
  name: string;
  email: string;
  isActive: boolean;
  usedFor: ProgramUsedFor;
}

export function programContactProblem(input: ProgramContactInput): string | null {
  if (!input.name.trim()) return "Enter the contact's name.";
  if (input.name.trim().length > 80) return "Keep the name under 80 characters.";
  if (!EMAIL.test(input.email.trim())) return "Enter a working email address.";
  return null;
}

export function cleanProgramContact(input: ProgramContactInput): ProgramContactInput {
  return {
    name: input.name.trim().replace(/\s+/g, " "),
    email: input.email.trim().toLowerCase(),
    isActive: input.isActive,
    usedFor: {
      jspAlerts: Boolean(input.usedFor.jspAlerts),
      trainingAlerts: Boolean(input.usedFor.trainingAlerts),
      observationAlerts: Boolean(input.usedFor.observationAlerts),
      eewpObserver: Boolean(input.usedFor.eewpObserver),
    },
  };
}

export function sortProgramContacts(list: ProgramContact[]): ProgramContact[] {
  return [...list].sort((a, b) => Number(b.isActive) - Number(a.isActive) || (a.name || a.email).localeCompare(b.name || b.email));
}

/** The same email twice in Program Contacts is refused. */
export function duplicateProgramContact(list: ProgramContact[], email: string, exceptId = ""): ProgramContact | null {
  const e = email.trim().toLowerCase();
  return list.find((c) => c.contactId !== exceptId && c.email.toLowerCase() === e) ?? null;
}

// ---- Section lines on the Settings menu ----

export function onOff(v: boolean): string {
  return v ? "on" : "off";
}

export function eewpLine(s: ProgramSettings): string {
  const req = EEWP_REQUIRED.filter((r) => s[r.field]).map((r) => r.label);
  return `${s.eewpEnabled ? "On" : "Off"} · email ${onOff(s.eewpNotifyEmail)}${req.length ? ` · required for ${req.join(", ")}` : ""}`;
}

export function limitsLine(s: ProgramSettings): string {
  return `${fmt(s.ieCeilingCal)} cal/cm² · ${fmt(s.twoPersonRuleVolts)} V · ${fmt(s.tamperSealAuditYears)} yr · ${fmt(s.taskAuthCycleDays)} days`;
}
